import React, { useState, useContext, useMemo } from 'react';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import allForm from '../../Stateless/Form';
import { COLORS } from '../../../theme/themeConstants';
import { HimmaContext } from '../../../context/HimmaContext';
import { useAllDriverManagement } from '../../Query/DriverManagerQuery';

const validationSchema = Yup.object().shape({
  reportType: Yup.string().required('Report Type is required'),
  fromDate: Yup.string().when('reportType', {
    is: 'custom',
    then: (schema) => schema.required('From Date is required for custom date range'),
    otherwise: (schema) => schema.nullable(),
  }),
  toDate: Yup.string().when('reportType', {
    is: 'custom',
    then: (schema) => schema.required('To Date is required for custom date range'),
    otherwise: (schema) => schema.nullable(),
  }),
  deliveryPartnerIds: Yup.array().of(Yup.string()).nullable(),
  driverStatus: Yup.array().of(Yup.string()).nullable(),
  settlementStatus: Yup.array().of(Yup.string()).nullable(),
  paymentMethods: Yup.array().of(Yup.string()).nullable(),
  orderStatuses: Yup.array().of(Yup.string()).nullable(),
});

const DriverPayoutReport: React.FC = () => {
  const api = useContext(HimmaContext)!;
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Fetch data from SDK
  const { data: driverData } = useAllDriverManagement({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  } as any);

  const reportTypeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'last30Days', label: 'Last 30 Days' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'custom', label: 'Custom' },
  ];

  const driverStatusOptions = [
    { value: 'blocked', label: 'Blocked' },
    { value: 'unblocked', label: 'Unblocked' },
  ];

  const settlementStatusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'INPROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  const paymentMethodOptions = [
    { value: 'cashOnDelivery', label: 'Cash on Delivery' },
    { value: 'onlinePayment', label: 'Online Payment' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'walletAndCashOnDelivery', label: 'Wallet + COD' },
    { value: 'walletAndOnlinePayment', label: 'Wallet + Online' },
  ];

  const orderStatusOptions = [
    { value: 'ORDER_DELIVERED', label: 'Order Delivered' },
    { value: 'DELIVERY_FAILED', label: 'Delivery Failed' },
  ];

  const driverNameOptions = useMemo(
    () =>
      (driverData?.data || []).map((item: any) => ({
        value: item.deliverBoyId,
        label:
          `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() ||
          'Unknown Driver',
      })),
    [driverData]
  );

  const formConfig = {
    initialValues: {
      reportType: '',
      fromDate: '',
      toDate: '',
      deliveryPartnerIds: [],
      driverStatus: [],
      settlementStatus: [],
      paymentMethods: [],
      orderStatuses: ['ORDER_DELIVERED'], // Default to ORDER_DELIVERED
    },
    validationSchema,
    onSubmit: async (values: Record<string, any>) => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        setInfoMessage(null);

        const {
          reportType,
          deliveryPartnerIds,
          driverStatus,
          settlementStatus,
          paymentMethods,
          orderStatuses,
        } = values;

        const fromDate = selectedFromDate ? selectedFromDate?.toISOString().split('T')[0] + 'T00:00:00Z' : undefined;
        const toDate = selectedToDate ? selectedToDate?.toISOString().split('T')[0] + 'T23:59:59Z' : undefined;

        // Validate that reportType is selected
        if (!reportType || reportType.trim() === '') {
          setErrorMessage('Report Type is required');
          setIsLoading(false);
          return;
        }

        // Map UI report type to API dateRange enum
        const dateRange = reportType;

        // Build the request object according to DriverPayoutReportRequest schema
        const driverPayoutReportRequest: any = {
          dateRange,
          orderStatuses: Array.isArray(orderStatuses) && orderStatuses.length > 0
            ? orderStatuses
            : ['ORDER_DELIVERED'], // Default to ORDER_DELIVERED
        };

        // Add optional filters
        if (Array.isArray(deliveryPartnerIds) && deliveryPartnerIds.length > 0) {
          driverPayoutReportRequest.deliveryPartnerIds = deliveryPartnerIds;
        }

        if (Array.isArray(driverStatus) && driverStatus.length > 0) {
          driverPayoutReportRequest.driverStatus = driverStatus;
        }

        if (Array.isArray(settlementStatus) && settlementStatus.length > 0) {
          driverPayoutReportRequest.settlementStatus = settlementStatus;
        }

        if (Array.isArray(paymentMethods) && paymentMethods.length > 0) {
          driverPayoutReportRequest.paymentMethods = paymentMethods;
        }

        // Add dateFrom and dateTo only if dateRange is 'custom'
        if (dateRange === 'custom') {
          if (!fromDate || !toDate) {
            setErrorMessage('Both From Date and To Date are required for custom date range');
            setIsLoading(false);
            return;
          }
          driverPayoutReportRequest.dateFrom = fromDate;
          driverPayoutReportRequest.dateTo = toDate;
        }

        const response = await api.downloadDriverPayoutReport(driverPayoutReportRequest, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `driver-payout-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccessMessage('Report downloaded successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        let errorMsg = '';
        
        // Try to parse error message from different response formats
        if (error?.response?.data) {
          // When responseType is 'blob', error responses might be Blob objects
          if (error.response.data instanceof Blob) {
            // Convert blob to text and parse JSON
            try {
              const text = await error.response.data.text();
              const parsed = JSON.parse(text);
              errorMsg = parsed.message || parsed.error || text;
            } catch (parseError) {
              // If parsing fails, try to get text anyway
              try {
                errorMsg = await error.response.data.text();
              } catch {
                errorMsg = 'Failed to download driver payout report. Please try again.';
              }
            }
          } else if (typeof error.response.data === 'string') {
            // If it's already a string, try to parse as JSON
            try {
              const parsed = JSON.parse(error.response.data);
              errorMsg = parsed.message || parsed.error || error.response.data;
            } catch {
              errorMsg = error.response.data;
            }
          } else if (error.response.data.message) {
            // If it's already an object with message
            errorMsg = error.response.data.message;
          } else if (error.response.data.error) {
            errorMsg = error.response.data.error;
          }
        }
        
        // Fallback to default error message
        if (!errorMsg) {
          errorMsg = error?.message || 'Failed to download driver payout report. Please try again.';
        }
        
        // Check if it's a 404 with "No data" message - show as info instead of error
        if (
          statusCode === 404 &&
          (errorMsg?.toLowerCase().includes('no data') ||
           errorMsg?.toLowerCase().includes('no driver payouts found') ||
           errorMsg?.toLowerCase().includes('not found') ||
           errorMsg?.toLowerCase().includes('no driver payouts') ||
           errorMsg?.toLowerCase().includes('matching the selected filters'))
        ) {
          // Show as info message instead of error
          setInfoMessage(errorMsg || 'No driver payouts found matching the selected filters and date range.');
          setTimeout(() => setInfoMessage(null), 5000);
        } else {
          // Show as error for other cases
          setErrorMessage(errorMsg);
        }
        console.error('Failed to download driver payout report', error);
      } finally {
        setIsLoading(false);
      }
    },
    validateOnChange: true,
    enableReinitialize: true,
    fields: [
      // Row 1: Report Type
      {
        name: 'reportType',
        label: 'Report Type:',
        type: 'select',
        options: reportTypeOptions,
        isRequired: true,
        component: ({ field, form }: any) => {
          // Clear date fields when switching away from custom
          const handleChange = (event: any) => {
            const newValue = event.target.value;
            form.setFieldValue(field.name, newValue);
            if (newValue !== 'custom') {
              form.setFieldValue('fromDate', '');
              form.setFieldValue('toDate', '');
              setSelectedFromDate(null);
              setSelectedToDate(null);
            }
          };
          
          return (
            <TextField
              {...field}
              select
              SelectProps={{
                native: true,
              }}
              onChange={handleChange}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  fontSize: 14,
                  minHeight: 56,
                },
              }}
            >
              <option value="">Select Report Type</option>
              {reportTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          );
        },
      },
      // Row 2: From Date | To Date
      {
        name: 'fromDate',
        label: 'From Date:',
        type: 'custom',
        dependsOn: {
          field: 'reportType',
          value: 'custom',
        },
        component: ({ field, form }: any) => {
          const reportType = form.values.reportType;
          const isCustom = reportType === 'custom';
          const hasError = form.touched.fromDate && !!form.errors.fromDate;
          
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedFromDate}
                onChange={(newValue: Dayjs | null) => {
                  setSelectedFromDate(newValue);
                  form.setFieldValue(
                    field.name,
                    newValue ? newValue.toISOString() : ''
                  );
                }}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    placeholder: 'Select start date',
                    helperText: hasError ? form.errors.fromDate : (isCustom ? 'Required for custom date range' : ''),
                    error: hasError,
                    required: isCustom,
                    onBlur: () => {
                      form.setFieldTouched(field.name, true);
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: 56,
                      },
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            </LocalizationProvider>
          );
        },
      },
      {
        name: 'toDate',
        label: 'To Date:',
        type: 'custom',
        dependsOn: {
          field: 'reportType',
          value: 'custom',
        },
        component: ({ field, form }: any) => {
          const reportType = form.values.reportType;
          const isCustom = reportType === 'custom';
          const hasError = form.touched.toDate && !!form.errors.toDate;
          
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedToDate}
                onChange={(newValue: Dayjs | null) => {
                  setSelectedToDate(newValue);
                  form.setFieldValue(
                    field.name,
                    newValue ? newValue.toISOString() : ''
                  );
                }}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    placeholder: 'Select end date',
                    helperText: hasError ? form.errors.toDate : (isCustom ? 'Required for custom date range' : ''),
                    error: hasError,
                    required: isCustom,
                    onBlur: () => {
                      form.setFieldTouched(field.name, true);
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: 56,
                      },
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            </LocalizationProvider>
          );
        },
      },
      // Row 3: Drivers
      {
        name: 'deliveryPartnerIds',
        label: 'Drivers:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = driverNameOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={driverNameOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select drivers (optional)"
                    helperText="Leave empty to include all drivers"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 4: Driver Status
      {
        name: 'driverStatus',
        label: 'Driver Status:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = driverStatusOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={driverStatusOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select driver status (optional)"
                    helperText="Leave empty to include all statuses"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 5: Settlement Status
      {
        name: 'settlementStatus',
        label: 'Settlement Status:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = settlementStatusOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={settlementStatusOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select settlement status (optional)"
                    helperText="Leave empty to include all statuses"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 6: Payment Methods
      {
        name: 'paymentMethods',
        label: 'Payment Methods:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = paymentMethodOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={paymentMethodOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select payment methods (optional)"
                    helperText="Leave empty to include all methods"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 7: Order Statuses
      {
        name: 'orderStatuses',
        label: 'Order Statuses:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = orderStatusOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={orderStatusOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select order statuses"
                    helperText="Default: Order Delivered"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
    ],
    customButton: () => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: COLORS.primary.main,
            textTransform: 'none',
            '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) },
            minWidth: 120,
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            'Download'
          )}
        </Button>
        <Button type="reset" variant="outlined" disabled={isLoading} sx={{color: COLORS.secondary.main, borderColor:COLORS.secondary.main,}}>
          Reset
        </Button>
      </Box>
    ),
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, marginBottom: 3, color: '#333', textAlign: 'left' }}
      >
        Driver Payout Report
      </Typography>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: 900,
          margin: '0 auto',
        }}
      >
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        {infoMessage && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfoMessage(null)}>
            {infoMessage}
          </Alert>
        )}
        {allForm(formConfig)()}
      </Box>
    </Box>
  );
};

export default DriverPayoutReport;
